import type { RevitFamily } from '../../api/revit/types';
import {
  familyHasCompatibleParameter,
  selectFamilyParameterMutationTargets,
} from '../../lib/revit/familyParameterTargetSelection';
import { sanitizeRevitParameterNames } from '../../lib/revitParameterNames';
import type { ConnectorParameterBindingKind } from '../../types';
import { resolveBoltParameterTypeHints } from './boltParameterTypes';
import {
  extractBoltParameterNames,
  findFamiliesWithObjectType,
  getTargetObjectType,
  type BoltConfigurationInput,
  type BoltModelingType,
  type BoltProjectParameterDescriptor,
} from './planner';

export function matchingFamiliesAlreadyContainParameters(
  families: RevitFamily[],
  targetObjectType: BoltModelingType,
  objectTypeParameterName: string,
  parameterNames: string[],
  bindingKind?: ConnectorParameterBindingKind,
  parameterTypeHints: Array<{ name: string; dataType: string }> = []
): boolean {
  const matchingFamilyIds = new Set(
    findFamiliesWithObjectType(
      families,
      targetObjectType,
      objectTypeParameterName
    ).map((family) => family.id)
  );
  if (matchingFamilyIds.size === 0) {
    return false;
  }

  const matchingFamilies = families.filter((family) => matchingFamilyIds.has(family.id));
  if (!bindingKind) {
    return matchingFamilies.every((family) =>
      parameterNames.every((parameterName) => {
        const expectedDataType = parameterTypeHints.find(
          (hint) => hint.name.trim().toLowerCase() === parameterName.trim().toLowerCase()
        )?.dataType;
        return familyHasCompatibleParameter(
          family,
          parameterName,
          undefined,
          expectedDataType
        );
      })
    );
  }

  return selectFamilyParameterMutationTargets(
    families,
    parameterNames,
    bindingKind,
    (family) => matchingFamilyIds.has(family.id),
    parameterTypeHints
  ).targetFamilyIds.length === 0;
}

export function boltProjectParametersAlreadyExist(
  parameterNames: string[],
  bindingKind: ConnectorParameterBindingKind,
  availableProjectParameters: BoltProjectParameterDescriptor[],
  parameterTypeHints: Array<{ name: string; dataType: string }> = []
): boolean {
  const requiredParameterType = bindingKind === 'instance' ? 'Instance' : 'Type';
  return sanitizeRevitParameterNames(parameterNames).every((parameterName) => {
    const expectedDataType = parameterTypeHints.find(
      (hint) => hint.name.trim().toLowerCase() === parameterName.trim().toLowerCase()
    )?.dataType.trim().toUpperCase();
    return availableProjectParameters.some((parameter) =>
      parameter.name.trim().toLowerCase() === parameterName.trim().toLowerCase() &&
      parameter.isProjectBinding === true &&
      parameter.parameterType === requiredParameterType &&
      (!expectedDataType || parameter.dataType?.trim().toUpperCase() === expectedDataType)
    );
  });
}

export function areBoltParametersAlreadyAvailable(
  configInput: BoltConfigurationInput,
  families: RevitFamily[],
  availableProjectParameters: BoltProjectParameterDescriptor[] = []
): boolean {
  const parameterNames = extractBoltParameterNames(configInput);
  if (parameterNames.length === 0) {
    return true;
  }

  const targetObjectType = getTargetObjectType(configInput.boltModelingChoice);
  if (findFamiliesWithObjectType(
    families,
    targetObjectType,
    configInput.objectTypeParameterName
  ).length === 0) {
    return false;
  }

  const bindingKind = configInput.parameterBindingKind === 'instance' ? 'instance' : 'type';
  if (configInput.parameterDestination === 'project') {
    return boltProjectParametersAlreadyExist(
      parameterNames,
      bindingKind,
      availableProjectParameters,
      resolveBoltParameterTypeHints(configInput)
    );
  }

  return matchingFamiliesAlreadyContainParameters(
    families,
    targetObjectType,
    configInput.objectTypeParameterName,
    parameterNames,
    bindingKind,
    resolveBoltParameterTypeHints(configInput)
  );
}
